import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { films, filmRegions, filmTypes } from "@/mocks/films";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilmGrid from "@/components/feature/FilmGrid";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { matchKeyword } from "@/utils/film";

const PAGE_SIZE = 8;
const ALL = "全部";
const decadeOptions = [ALL, "2024", "2023", "2022", "2021"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { favorites, toggleFavorite } = useFavorites();

  const keyword = searchParams.get("q") ?? "";
  const typeParam = searchParams.get("type") ?? ALL;
  const region = searchParams.get("region") ?? ALL;
  const decade = searchParams.get("decade") ?? ALL;
  const page = Math.max(1, Number(searchParams.get("page") ?? "1"));

  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta(
    keyword ? `搜索“${keyword}” - 伦理片在线观看` : "搜索影视作品 - 伦理片在线观看",
    "在伦理片在线观看平台搜索伦理电影、电视剧及短片，按类型、地区、年代筛选，快速找到你感兴趣的伦理影视作品。",
  );

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === ALL) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    next.delete("page");
    setSearchParams(next);
  };

  const results = useMemo(() => {
    let list = films.filter((f) => matchKeyword(f, keyword));
    if (typeParam !== ALL) list = list.filter((f) => f.type === typeParam);
    if (region !== ALL) list = list.filter((f) => f.region === region);
    if (decade !== ALL) list = list.filter((f) => String(f.year) === decade);
    return [...list].sort((a, b) => b.popularity - a.popularity);
  }, [keyword, typeParam, region, decade]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasActiveFilter = typeParam !== ALL || region !== ALL || decade !== ALL;
  const hotFilms = useMemo(
    () => [...films].sort((a, b) => b.popularity - a.popularity).slice(0, 4),
    [],
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-3xl font-bold text-foreground-950">搜索影视作品</h1>
        <p className="mt-3 text-sm text-foreground-600">
          支持按片名、导演、主演、题材与简介关键词检索伦理影视内容。
        </p>
      </header>

      <form
        onSubmit={submitSearch}
        className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center md:p-5"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
          <i className="ri-search-line text-secondary-500"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入片名、导演、主演或题材，例如“家庭”"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
          />
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-2-line"></i>
          搜索
        </button>
      </form>

      {keyword && (
        <p className="mt-4 text-sm text-secondary-600">
          当前搜索词：
          <span className="ml-1 rounded-md bg-accent-50 px-2 py-0.5 font-medium text-accent-700">
            {keyword}
          </span>
        </p>
      )}

      <div className="mt-5">
        <FilterBar
          groups={[
            { key: "type", label: "类型", options: [ALL, ...filmTypes.map((t) => t.label)] },
            { key: "region", label: "地区", options: [ALL, ...filmRegions] },
            { key: "decade", label: "年代", options: decadeOptions },
          ]}
          values={{
            type: typeParam === ALL ? ALL : filmTypes.find((t) => t.key === typeParam)?.label ?? ALL,
            region,
            decade,
          }}
          onChange={(key, value) => {
            if (key === "type") {
              const found = filmTypes.find((t) => t.label === value);
              updateParam("type", found ? found.key : ALL);
            } else {
              updateParam(key, value);
            }
          }}
          onReset={() => {
            const next = new URLSearchParams();
            if (keyword) next.set("q", keyword);
            setSearchParams(next);
          }}
          hasActiveFilter={hasActiveFilter}
        />
      </div>

      <div className="mt-6 border-b border-background-200 pb-3">
        <p className="text-sm text-secondary-600">
          共找到 <strong className="text-primary-700">{results.length}</strong> 条结果
        </p>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-6">
            <FilmGrid films={pageItems} favorites={favorites} onToggleFavorite={toggleFavorite} />
          </div>
          <Pagination
            current={safePage}
            total={totalPages}
            onChange={(p) => updateParam("page", String(p))}
          />
        </>
      ) : (
        <section className="mt-8">
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-secondary-500">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
              没有找到相关作品
            </h3>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              换个关键词试试，或看看下面的热门推荐内容。
            </p>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams();
                  if (keyword) next.set("q", keyword);
                  setSearchParams(next);
                }}
                className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                清空筛选条件
              </button>
            )}
          </div>

          <div className="mt-10">
            <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                <i className="ri-fire-line text-lg"></i>
              </span>
              热门推荐
            </h2>
            <div className="mt-6">
              <FilmGrid films={hotFilms} favorites={favorites} onToggleFavorite={toggleFavorite} />
            </div>
          </div>
        </section>
      )}

      {results.length > 0 && !keyword && (
        <p className="mt-4 text-center text-xs text-secondary-400">
          未输入关键词，当前展示平台全部作品。
        </p>
      )}

      <Link
        to="/category/all"
        className="mt-8 flex items-center justify-center gap-1.5 text-sm text-primary-600 transition-colors hover:text-primary-700"
      >
        浏览全部作品
        <i className="ri-arrow-right-line"></i>
      </Link>
    </div>
  );
}